import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HasMultiOrderInstrumentPipe } from '@zero/shared/ui-pipes';
import { ModalComponent } from '@zero/shared/ui-web-design-system';
import { PositionsFacade } from '@zero/shared/util-global-state';
import { Asset, InstrumentType, Position } from '@zero/shared/util-models';
import { AssetSearchService } from '@zero/shared/util-services';
import { MultiOrderInstrument } from '@zero/trade/domain';
import { combineLatest, firstValueFrom, map, startWith } from 'rxjs';

@Component({
  selector: 'trade-multi-order-positions',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoPipe, AsyncPipe, HasMultiOrderInstrumentPipe, ReactiveFormsModule, ModalComponent],
  templateUrl: './multi-order-positions.component.html',
  styleUrl: './multi-order-positions.component.scss',
})
export class MultiOrderPositionsComponent implements OnInit {
  @Input() public currentInstruments: MultiOrderInstrument[] = [];

  @Output() public positionPicked = new EventEmitter<{ position: Position; asset: Asset }>();

  public readonly positionsFacade = inject(PositionsFacade);
  public readonly activeModal = inject(NgbActiveModal);
  public readonly assetSearchService = inject(AssetSearchService);

  public error = '';
  public disallowedInstrumentType = InstrumentType.Crypto;
  public currentFilter = new FormControl();
  public isPicking = false;

  ngOnInit(): void {
    this.positionsFacade.loadPositions(true);
  }

  #groupPositions = (positions: Position[]) => {
    const grouped = positions
      .filter((position) => !!position.type)
      .reduce((grouped, position) => {
        if (!grouped[position.type]) {
          grouped[position.type] = [];
        }
        grouped[position.type].push(position);
        return grouped;
      }, {});

    return Object.keys(grouped).map((key) => ({
      type: key,
      positions: grouped[key]
        ? grouped[key].sort((a: { longName: string }, b: { longName: string }) => a?.longName?.localeCompare(b?.longName))
        : [],
    }));
  };

  public groupedPositions$ = combineLatest({
    positions: this.positionsFacade.allPositions$,
    currentFilter: this.currentFilter.valueChanges.pipe(startWith('')),
  }).pipe(
    map(({ positions, currentFilter }) => {
      if (!currentFilter) {
        return this.#groupPositions(positions);
      }

      this.error = '';
      const filter = currentFilter.trim().toLowerCase();
      return this.#groupPositions(
        positions.filter(({ isin, longName }) => isin?.toLowerCase().includes(filter) || longName?.toLowerCase().includes(filter)),
      );
    }),
  );

  public pickPosition = async (position: Position) => {
    if (position.type === this.disallowedInstrumentType) {
      this.isPicking = false;
      return;
    }

    this.isPicking = true;
    const relevantInstruments = this.currentInstruments?.filter((instrument) => instrument.direction === 'sell');
    if (relevantInstruments?.some((instrument) => instrument.isin === position.isin)) {
      this.error = 'trade.multi-order.positions.errors.duplicate';
      this.isPicking = false;
      return;
    }

    const asset = await firstValueFrom(this.assetSearchService.loadAssetByIsin(position.isin));
    this.positionPicked.emit({ position, asset });
    this.activeModal.close();
    this.isPicking = false;
  };
}
